import React, { useState } from 'react';
import moment from 'moment-timezone';
import { MainLayout } from '../layouts';

// Mapping of Australian states to their IANA timezone identifiers and offsets
const stateToTimezone: Record<string, { timezone: string, offset: string }> = {
  'nsw': { timezone: 'Australia/Sydney', offset: '+10:00' },
  'vic': { timezone: 'Australia/Melbourne', offset: '+10:00' },
  'qld': { timezone: 'Australia/Brisbane', offset: '+10:00' },
  'sa': { timezone: 'Australia/Adelaide', offset: '+09:30' },
  'wa': { timezone: 'Australia/Perth', offset: '+08:00' },
  'tas': { timezone: 'Australia/Hobart', offset: '+10:00' },
  'act': { timezone: 'Australia/Sydney', offset: '+10:00' },
  'nt': { timezone: 'Australia/Darwin', offset: '+09:30' },
  'hk': { timezone: 'Asia/Hong_Kong', offset: '+08:00' },
  'nz': { timezone: 'Pacific/Auckland', offset: '+12:00' },
};

const TimezoneTestPage: React.FC = () => {
  const [selectedState, setSelectedState] = useState<string>('nsw');
  const [raceTime, setRaceTime] = useState<string>('11:00');
  const [raceDate, setRaceDate] = useState<string>(moment().format('YYYY-MM-DD'));
  
  // Calculate the timezone conversions
  const { timezone: sourceTimezone, offset: sourceOffset } = stateToTimezone[selectedState];
  
  // Create ISO 8601 formatted time string with timezone offset
  // Example: "2025-04-26T11:00:00+10:00"
  const isoDateTime = `${raceDate}T${raceTime}:00${sourceOffset}`;
  
  // Parse the ISO 8601 time string
  const sourceTime = moment(isoDateTime);
  
  // Convert to local timezone
  const localTime = sourceTime.clone().local();
  
  // Get all Australian timezones for comparison
  const allTimezones = Object.entries(stateToTimezone).map(([state, { timezone }]) => {
    const timeInZone = sourceTime.clone().tz(timezone);
    return {
      state: state.toUpperCase(),
      timezone,
      time: timeInZone.format('h:mm A'),
      offset: timeInZone.format('Z')
    };
  });
  
  return (
    <MainLayout>
      <div className="p-4">
        <div className="bg-white rounded-lg shadow-card mb-6 p-4">
          <h1 className="text-2xl font-bold mb-4">Timezone Conversion Test</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Race State
              </label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              >
                {Object.keys(stateToTimezone).map((state) => (
                  <option key={state} value={state}>
                    {state.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Race Date
              </label>
              <input
                type="date"
                value={raceDate}
                onChange={(e) => setRaceDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Race Time (in {selectedState.toUpperCase()})
              </label>
              <input
                type="time"
                value={raceTime}
                onChange={(e) => setRaceTime(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
          </div>
          
          <div className="bg-racing-gray p-4 rounded mb-6">
            <h2 className="text-lg font-bold mb-2">Conversion Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold">Source Time:</p>
              <p>{sourceTime.format('YYYY-MM-DD h:mm A')} ({selectedState.toUpperCase()})</p>
              <p className="text-sm text-gray-600">ISO Format: {isoDateTime}</p>
              <p className="text-sm text-gray-600">Timezone: {sourceTimezone}</p>
            </div>
              
              <div>
                <p className="font-semibold">Your Local Time:</p>
                <p>{localTime.format('YYYY-MM-DD h:mm A')}</p>
                <p className="text-sm text-gray-600">Timezone: {moment.tz.guess()}</p>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-lg font-bold mb-2">Time in All Australian States</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {allTimezones.map((tz) => (
                <div key={tz.state} className="border border-racing-gray-darker rounded p-3">
                  <p className="font-bold">{tz.state}</p>
                  <p>{tz.time}</p>
                  <p className="text-sm text-gray-600">{tz.timezone} ({tz.offset})</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TimezoneTestPage;
